import { BlocksGraph, BlocksLinksAggregation, BlocksReference } from '../types';

/**
 * Which ends of its route a segment touches. The first segment leaves the source, the last
 * arrives at the target, and a two-waypoint route is one segment that does both.
 */
export type BusSegment = {
  readonly atSource: boolean;
  readonly atTarget: boolean;
};

/** The end of a reference that its siblings bundle at, when they bundle at all. */
type BundleEnd = 'source' | 'target';

const uniqueBusId = (link: BlocksReference) =>
  `${link.source.id}${link.target.id}${link.modelId}`;

const PARENT_CHILD_BUS = 'parentchild';

const typeIdOf = (link: BlocksReference) =>
  link.globalTypeId ?? PARENT_CHILD_BUS;

/**
 * Separates the parts of a composed key. Ids and type ids are opaque, so a separator either of
 * them could contain would let two different pairs compose one key. Escaped rather than written
 * literally: a raw NUL byte makes git treat the file as binary and stops it rendering a diff.
 */
const KEY_SEPARATOR = '\u0000';

/** Two references can only bundle if they share a type as well as an end. */
const endKey = (nodeId: string, typeId: string) =>
  `${nodeId}${KEY_SEPARATOR}${typeId}`;

/**
 * The end each reference bundles at: the one where it has more siblings of its own type, because
 * that is where merging saves the most lanes. Ties go to the source, so a symmetric shape reads as
 * one thing fanning out rather than many things converging — on the four-by-four mesh every
 * reference ties at four, and the result is one departure per source with each arrival its own.
 *
 * A reference bundles at *one* end. Sharing a lane at both would merge every reference of the type
 * into a single trunk: the departures coalesce, the arrivals coalesce, and the run between them
 * coalesces too, so a complete bipartite graph and a perfect matching draw identically. Choosing an
 * end is what keeps the other end's fan legible.
 */
const bundleEnds = (graph: BlocksGraph): Map<BlocksReference, BundleEnd> => {
  const fromCounts = new Map<string, number>();
  const toCounts = new Map<string, number>();

  for (const link of graph.edges) {
    const typeId = typeIdOf(link);
    const from = endKey(link.source.id, typeId);
    const to = endKey(link.target.id, typeId);

    fromCounts.set(from, (fromCounts.get(from) ?? 0) + 1);
    toCounts.set(to, (toCounts.get(to) ?? 0) + 1);
  }

  const ends = new Map<BlocksReference, BundleEnd>();
  for (const link of graph.edges) {
    const typeId = typeIdOf(link);
    const siblingsAtSource =
      fromCounts.get(endKey(link.source.id, typeId)) ?? 1;
    const siblingsAtTarget = toCounts.get(endKey(link.target.id, typeId)) ?? 1;

    ends.set(link, siblingsAtSource >= siblingsAtTarget ? 'source' : 'target');
  }

  return ends;
};

export const getGetBusId = (
  graph: BlocksGraph,
  linksAggregation: BlocksLinksAggregation
) => {
  /* Only `byReferenceType` bundles, so only it needs the choice of end. */
  const ends =
    linksAggregation === 'byReferenceType'
      ? bundleEnds(graph)
      : new Map<BlocksReference, BundleEnd>();

  const getBusId = (link: BlocksReference, segment: BusSegment) => {
    if (link.source !== link.sourceProxy || link.target !== link.targetProxy) {
      /* this is supposed to emulate reference "coalescing". it doesn't do a very good job */

      if (link.sourceProxy?.modelId && link.targetProxy?.modelId) {
        const sourceId = link.sourceProxy.modelId;
        const targetId = link.targetProxy.modelId;
        const ret =
          link.source === link.sourceProxy
            ? targetId
            : link.target === link.targetProxy
              ? sourceId
              : `${sourceId}_${targetId}`;

        return ret;
      }
    }

    switch (linksAggregation) {
      default:
      case 'hide':
        /* should not happen */
        return uniqueBusId(link);

      case 'separate':
        return uniqueBusId(link);

      case 'byReferenceType': {
        /* A self-reference has one end, so it has no sibling to bundle with. */
        if (link.source === link.target) return uniqueBusId(link);

        const bundleAt = ends.get(link);

        /* The trunk runs from the chosen end as far as it can, and each reference peels off only on
           its final approach. Diverging at the first turn instead draws a fan-out's twelve
           references as twelve stacked lanes rather than one comb. A segment touching both ends is
           the whole route, so it stays on the trunk. */
        const isFinalApproach =
          bundleAt === 'source'
            ? segment.atTarget && !segment.atSource
            : segment.atSource && !segment.atTarget;

        /* Off the trunk a reference keeps a lane of its own, which is what makes the fan traceable.
           Non-overlapping segments still share a lane: the interval tree only rejects an overlap
           carrying a different id. */
        if (isFinalApproach) return uniqueBusId(link);

        const endpoint = bundleAt === 'source' ? link.source : link.target;

        return [typeIdOf(link), bundleAt, endpoint.id].join(KEY_SEPARATOR);
      }

      case 'all':
        return 'all';
    }
  };

  return getBusId;
};
